import type { ComboboxKeyboard } from './useComboboxKeyboard';

/**
 * Suggestion listbox shared by the two registry-backed comune comboboxes
 * (`ComunePicker` and `LocalitaPopup`), T015-FE.
 *
 * Holds the WAI-ARIA wiring in ONE place: the listbox stays mounted (hidden)
 * so the input's `aria-controls` always resolves, the highlight travels via
 * `aria-activedescendant` (`combobox.optionProps`), and each row is a plain
 * option — the keyboard model itself lives in `useComboboxKeyboard`.
 *
 * Styling stays with the consumer: both CSS modules carry the same row rules,
 * so the caller passes its own class map instead of sharing a stylesheet that
 * would couple what each surface can tune.
 */
export interface ComuneSuggestionOption {
  id: string;
  name: string;
  province: string;
}

export interface ComuneSuggestionListProps<O extends ComuneSuggestionOption> {
  /** DOM id of the listbox (the input's `aria-controls`). */
  listId: string;
  /** Accessible name of the listbox. */
  label: string;
  combobox: ComboboxKeyboard;
  /** Filtered options currently offered. */
  options: readonly O[];
  onSelect: (option: O) => void;
  /**
   * The consumer's CSS-module class map. Both modules style the rows with the
   * same four names: `suggestionsDropdown`, `suggestionItem`,
   * `suggestionItemActive`, `suggestionProvince`.
   */
  classes: { readonly [key: string]: string };
}

export function ComuneSuggestionList<O extends ComuneSuggestionOption>({
  listId,
  label,
  combobox,
  options,
  onSelect,
  classes,
}: ComuneSuggestionListProps<O>) {
  return (
    <ul
      id={listId}
      className={classes.suggestionsDropdown}
      role="listbox"
      aria-label={label}
      hidden={!combobox.open}
    >
      {combobox.open &&
        options.map((option, index) => {
          const active = combobox.activeIndex === index;
          return (
            <li
              key={option.id}
              {...combobox.optionProps(index)}
              className={
                active
                  ? `${classes.suggestionItem} ${classes.suggestionItemActive}`
                  : classes.suggestionItem
              }
              role="option"
              onClick={() => onSelect(option)}
            >
              <span>{option.name}</span>
              <span className={classes.suggestionProvince}>({option.province})</span>
            </li>
          );
        })}
    </ul>
  );
}
